"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { MapView } from "@/components/map/MapView";
import type { MapMarker, MapPlan, MapTrack } from "@/components/map/types";
import { copyText } from "@/lib/clipboard";
import {
  coastMountainHint, DAY_HOURS, DAY_INTERESTS, decodePlan, decodePolyline, encodePlan, fmtDistance, fmtDuration,
  PLAN_KEY, PLAN_KEY_LGBT, savePlan, suggestDay, TRAVEL_MODES,
  type DaySuggestion, type PlannerPlace, type PlannerState, type RouteResult, type TodayWeather, type TravelMode,
} from "@/lib/route-shared";
import { onIsland, PLACE_TYPES, type PlaceType } from "@/lib/travel-shared";
import { WEATHER_REGIONS, WEATHER_REGION_KEYS, type WeatherRegion } from "@/lib/weather-shared";
import { toggleStop, updatePlan, usePlan } from "./store";
import "./route.css";

type Props = {
  label: string; markers: MapMarker[]; tracks: MapTrack[]; focus?: string;
  places: PlannerPlace[]; maxStops: number; factor: number;
  /** Heutige Vorhersage der drei Zonen. Fehlt sie, fehlt der Wetterhinweis. */
  today?: TodayWeather;
  /** Abendplaner: eigener Speicher, nur Orte der Themenwelt, Teilen-Link unter /lgbt/planen. */
  scope?: "general" | "lgbt";
};
type Status = { kind: "idle" | "busy" | "error"; text: string };

const requestKey = (p: PlannerState) => JSON.stringify([p.start && (p.start.placeId ?? [p.start.lat, p.start.lng]), p.stops.map((s) => s.id), p.mode, p.optimize, p.back]);
const GOOGLE_MODE: Record<TravelMode, string> = { auto: "driving", bicycle: "bicycling", pedestrian: "walking" };
const GOOGLE_MAX_WAYPOINTS = 9;

function gpx(points: { name: string; lat: number; lng: number }[], line: [number, number][]) {
  const x = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="GranCanariaGuide" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata><name>Route auf Gran Canaria</name></metadata>
${points.map((p) => `  <wpt lat="${p.lat.toFixed(6)}" lon="${p.lng.toFixed(6)}"><name>${x(p.name)}</name></wpt>`).join("\n")}
  <trk><name>Route auf Gran Canaria</name><trkseg>
${line.map(([lat, lng]) => `    <trkpt lat="${lat.toFixed(6)}" lon="${lng.toFixed(6)}"/>`).join("\n")}
  </trkseg></trk>
</gpx>
`;
}

const thin = (pts: [number, number][], max: number) => {
  const step = Math.max(1, Math.ceil(pts.length / max));
  return pts.filter((_, i) => i % step === 0 || i === pts.length - 1).map(([a, b]) => [Number(a.toFixed(5)), Number(b.toFixed(5))]);
};

/** Inselkarte mit Routenplaner. Die Planung bleibt im Browser; berechnet wird erst nach einem Klick. */
export function RoutePlanner({ label, markers, tracks, focus, places, maxStops, factor, today, scope = "general" }: Props) {
  const storageKey = scope === "lgbt" ? PLAN_KEY_LGBT : PLAN_KEY;
  const sharePath = scope === "lgbt" ? "/lgbt/planen" : "/routen";
  const stored = usePlan(storageKey);
  const write = (patch: Partial<PlannerState>) => updatePlan(patch, storageKey);
  const plan = stored ?? { start: null, stops: [], mode: "auto" as TravelMode, optimize: false, back: false };
  const byId = useMemo(() => new Map(places.map((p) => [p.id, p])), [places]);
  const stops = plan.stops.filter((s) => byId.has(s.id));
  const [picking, setPicking] = useState(false);
  const [live, setLive] = useState(false);
  const [result, setResult] = useState<{ key: string; r: RouteResult } | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle", text: "" });
  const [note, setNote] = useState("");
  const [share, setShare] = useState("");
  const [google, setGoogle] = useState(false);
  const [fit, setFit] = useState(0);
  const [drag, setDrag] = useState<number | null>(null);
  const [dayBase, setDayBase] = useState<WeatherRegion>("sueden");
  const [dayHours, setDayHours] = useState<(typeof DAY_HOURS)[number]>(6);
  const [dayInterests, setDayInterests] = useState<string[] | null>(null);
  const [dayNote, setDayNote] = useState("");
  const [pendingDay, setPendingDay] = useState<DaySuggestion | null>(null);
  const abort = useRef<AbortController | null>(null);
  const pdfForm = useRef<HTMLFormElement>(null);

  // Teilen-Link (#route=1&…) übernehmen; das Fragment geht nie an den Server.
  useEffect(() => {
    const fromHash = decodePlan(window.location.hash, places);
    if (fromHash) {
      savePlan(fromHash, storageKey);
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, [places, storageKey]);

  const key = requestKey({ ...plan, stops });
  const current = result?.key === key ? result.r : null;
  const ready = !!plan.start && stops.length > 0;

  const calculate = async () => {
    if (!plan.start || !stops.length) return;
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setLive(true);
    setStatus({ kind: "busy", text: "Route wird berechnet …" });
    const sent = { ...plan, stops };
    try {
      const res = await fetch("/api/route", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        signal: ctl.signal,
        body: JSON.stringify({
          start: sent.start!.placeId ? { place_id: sent.start!.placeId } : { lat: sent.start!.lat, lon: sent.start!.lng },
          stops: sent.stops.map((s) => ({ place_id: s.id, ...(s.dwell != null ? { dwell_minutes: s.dwell } : {}) })),
          mode: sent.mode, optimize: sent.optimize, return_to_start: sent.back,
          ...(scope === "lgbt" ? { theme: "lgbt" } : {}),
        }),
      });
      const json = (await res.json().catch(() => null)) as (RouteResult & { error?: string }) | null;
      if (!res.ok || !json || json.error) {
        setStatus({ kind: "error", text: json?.error ?? "Die Route konnte nicht berechnet werden." });
        return;
      }
      let r: RouteResult = json;
      let next = sent;
      if (sent.optimize && r.order.some((v, i) => v !== i)) {
        // Optimierte Reihenfolge übernehmen, damit Liste, Pins und Etappen übereinstimmen.
        const pos = new Map(r.order.map((old, i) => [old, i]));
        next = { ...sent, stops: r.order.map((i) => sent.stops[i]) };
        r = { ...r, order: r.order.map((_, i) => i), legs: r.legs.map((l) => ({ ...l, from: l.from < 0 ? -1 : pos.get(l.from)!, to: l.to < 0 ? -1 : pos.get(l.to)! })) };
        savePlan(next, storageKey);
        setNote(`Reihenfolge optimiert: ${next.stops.map((s) => byId.get(s.id)?.name).join(", ")}.`);
      }
      setResult({ key: requestKey(next), r });
      setFit((n) => n + 1);
      setStatus({ kind: "idle", text: "" });
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      setStatus({ kind: "error", text: "Der Routenplaner ist gerade nicht erreichbar. Bitte später noch einmal versuchen." });
    }
  };
  const calcRef = useRef(calculate);
  useEffect(() => {
    calcRef.current = calculate;
  });

  // Nach der ersten Berechnung neu rechnen, sobald sich Start, Stopps oder Optionen ändern.
  useEffect(() => {
    if (!live || !ready || result?.key === key) return;
    const t = setTimeout(() => void calcRef.current(), 500);
    return () => clearTimeout(t);
  }, [live, ready, key, result?.key]);

  const say = (text: string) => setNote(text);
  const setStops = (list: PlannerState["stops"], manual = true) => write({ stops: list, ...(manual && plan.optimize ? { optimize: false } : {}) });
  const toggle = (id: number) => {
    const name = byId.get(id)?.name ?? "Ort";
    const had = stops.some((s) => s.id === id);
    if (!toggleStop(id, maxStops, storageKey)) return say(`Höchstens ${maxStops} Stopps pro Route.`);
    say(had ? `${name} aus der Route entfernt.` : `${name} zur Route hinzugefügt.`);
  };
  const move = (from: number, to: number) => {
    if (to < 0 || to >= stops.length || from === to) return;
    const list = [...stops];
    const [it] = list.splice(from, 1);
    list.splice(to, 0, it);
    setStops(list);
    say(`${byId.get(it.id)?.name} ist jetzt Stopp ${to + 1}.`);
  };
  const setDwell = (id: number, v: string) => {
    const n = v === "" ? undefined : Math.max(0, Math.min(720, Math.round(Number(v))));
    setStops(stops.map((s) => (s.id === id ? { id, ...(n != null && Number.isFinite(n) ? { dwell: n } : {}) } : s)), false);
  };
  const dwellOf = (s: PlannerState["stops"][number]) => s.dwell ?? byId.get(s.id)?.dwell ?? 0;
  const dwellSum = stops.reduce((n, s) => n + dwellOf(s) * 60, 0);

  const setStart = (s: PlannerState["start"]) => {
    write({ start: s });
    setPicking(false);
  };
  const locate = () => {
    if (!navigator.geolocation) return say("Dein Browser gibt keinen Standort heraus.");
    say("Standort wird abgefragt …");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        if (!onIsland(lat, lng)) return say("Dein Standort liegt nicht auf Gran Canaria. Wähle den Start auf der Karte oder aus der Liste.");
        setStart({ lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)), label: "Mein Standort" });
        say("Startpunkt auf deinen Standort gesetzt.");
      },
      () => say("Der Standort ist nicht verfügbar oder wurde nicht freigegeben."),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  };
  const onMapClick = (lat: number, lng: number) => {
    if (!onIsland(lat, lng)) return say("Der Startpunkt muss auf Gran Canaria liegen.");
    setStart({ lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)), label: "Punkt auf der Karte" });
    say("Startpunkt gesetzt.");
  };

  const lines = useMemo(() => (current ? current.shape.map((s) => decodePolyline(s)) : []), [current]);
  const mapPlan: MapPlan = { start: plan.start, stops: stops.map((s) => s.id), lines };
  const name = (i: number) => (i < 0 ? plan.start?.label || "Start" : byId.get(stops[i]?.id)?.name ?? "Stopp");
  const points = [
    ...(plan.start ? [{ name: plan.start.label || "Start", lat: plan.start.lat, lng: plan.start.lng }] : []),
    ...stops.map((s) => byId.get(s.id)!).map((p) => ({ name: p.name, lat: p.lat, lng: p.lng })),
  ];

  const shareLink = () => {
    const url = `${window.location.origin}${sharePath}#${encodePlan({ ...plan, stops })}`;
    setShare(url);
    void copyText(url).then((ok) => say(ok ? "Link kopiert. Er enthält Start und Stopps nur im Teil nach #." : "Link erstellt; bitte von Hand kopieren."));
  };
  const downloadGpx = () => {
    const blob = new Blob([gpx(points, lines.flat())], { type: "application/gpx+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "route-gran-canaria.gpx";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const pdfData = current ? JSON.stringify({
    mode: plan.mode, back: plan.back, factor: current.factor,
    start: { label: plan.start?.label || "Start", lat: plan.start?.lat, lng: plan.start?.lng },
    stops: stops.map((s) => ({ id: s.id, dwell: dwellOf(s) })),
    legs: current.legs.map((l) => ({ to: l.to, s: l.seconds, m: l.meters, w: l.winding })),
    line: thin(lines.flat(), 400),
    ...(scope === "lgbt" ? { theme: "lgbt" } : {}),
  }) : "";
  const googleUrl = () => {
    if (!plan.start) return "";
    const pt = (p: { lat: number; lng: number }) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
    const way = points.slice(1);
    const dest = plan.back ? points[0] : way.pop();
    if (!dest) return "";
    const q = new URLSearchParams({ api: "1", origin: pt(plan.start), destination: pt(dest), travelmode: GOOGLE_MODE[plan.mode] });
    if (way.length) q.set("waypoints", way.slice(0, GOOGLE_MAX_WAYPOINTS).map(pt).join("|"));
    return `https://www.google.com/maps/dir/?${q.toString()}`;
  };
  const tooManyForGoogle = points.length - 1 - (plan.back ? 0 : 1) > GOOGLE_MAX_WAYPOINTS;

  const interestChoices = DAY_INTERESTS.filter((t) => places.some((p) => p.type === t));
  const chosenInterests = (dayInterests ?? [...interestChoices]).filter((t) => interestChoices.includes(t as (typeof DAY_INTERESTS)[number]));
  const toggleInterest = (t: string) => {
    const cur = new Set(chosenInterests);
    if (cur.has(t)) cur.delete(t); else cur.add(t);
    setDayInterests([...cur]);
  };
  const applyDay = (result: DaySuggestion) => {
    write({ start: result.start, stops: result.stops, optimize: false });
    setPendingDay(null);
    setDayNote("");
    setLive(false);
    setResult(null);
    say("Vorschlag steht in der Route. Mit „Route berechnen“ siehst du die Zeiten.");
  };
  const proposeDay = () => {
    const result = suggestDay({ places, base: dayBase, hours: dayHours, interests: chosenInterests, mode: plan.mode, maxStops, start: plan.start });
    if (!result.stops.length) {
      setPendingDay(null);
      setDayNote(result.note);
      return;
    }
    setDayNote("");
    if (stops.length) setPendingDay(result);
    else applyDay(result);
  };
  const stopZones = stops.map((s) => byId.get(s.id)?.zone).filter((z): z is WeatherRegion => !!z);
  const weatherLine = scope === "general" && today ? coastMountainHint(dayBase, stopZones, today) : null;

  const hotels = places.filter((p) => p.type === "hotel");
  const others = places.filter((p) => p.type !== "hotel");
  const free = places.filter((p) => !stops.some((s) => s.id === p.id));
  const groups = [...new Set(free.map((p) => p.type))];

  return (
    <div className="rp">
      <div className="rp-map">
        <MapView mode="full" label={label} markers={markers} tracks={tracks} focus={focus}
          plan={mapPlan} fitPlan={fit} picking={picking} onPlanToggle={toggle} onMapClick={onMapClick} />
      </div>
      <section id="routenplaner" className="rp-panel" aria-labelledby="rp-title">
            <div className="rp-head">
              <h2 id="rp-title">Deine Route</h2>
            </div>
            <p className="sr-only" role="status">{note}</p>
            <div className="rp-body">
              {scope === "general" && interestChoices.length > 0 && (
                <fieldset className="rp-block">
                  <legend>Tag vorschlagen</legend>
                  <div className="rp-modes" role="radiogroup" aria-label="Basis">
                    {WEATHER_REGION_KEYS.map((k) => (
                      <label key={k} className="rp-chip"><input type="radio" name="rp-base" checked={dayBase === k} onChange={() => setDayBase(k)} /> {WEATHER_REGIONS[k].name}</label>
                    ))}
                  </div>
                  <div className="rp-modes" role="radiogroup" aria-label="Stunden">
                    {DAY_HOURS.map((h) => (
                      <label key={h} className="rp-chip"><input type="radio" name="rp-hours" checked={dayHours === h} onChange={() => setDayHours(h)} /> {h} Stunden</label>
                    ))}
                  </div>
                  <div className="rp-modes">
                    {interestChoices.map((t) => (
                      <label key={t} className="rp-chip"><input type="checkbox" checked={chosenInterests.includes(t)} onChange={() => toggleInterest(t)} /> {PLACE_TYPES[t].label}</label>
                    ))}
                  </div>
                  <p className="rp-muted">Unterwegs mit {TRAVEL_MODES[plan.mode]}. Die Auswahl füllt etwa drei Viertel der Zeit, die echte Strecke kommt erst mit „Route berechnen“.</p>
                  {weatherLine && <p className="rp-weather">{weatherLine}</p>}
                  {dayNote && <p className="rp-muted" role="status">{dayNote}</p>}
                  {pendingDay ? (
                    <div className="rp-row">
                      <p className="rp-muted">Die vorhandenen Stopps werden ersetzt.</p>
                      <button type="button" className="tbutton rp-go" onClick={() => applyDay(pendingDay)}>Ersetzen</button>
                      <button type="button" className="rp-btn" onClick={() => setPendingDay(null)}>Abbrechen</button>
                    </div>
                  ) : (
                    <button type="button" className="tbutton rp-go" onClick={proposeDay}>Tag vorschlagen</button>
                  )}
                </fieldset>
              )}
              <fieldset className="rp-block">
                <legend>Startpunkt</legend>
                <p className="rp-start">{plan.start ? <><span className="rp-num rp-s" aria-hidden="true">S</span>{plan.start.label || "Gewählter Punkt"}</> : "Noch kein Startpunkt gewählt."}</p>
                <div className="rp-row">
                  <button type="button" className="rp-btn" aria-pressed={picking} onClick={() => { setPicking(!picking); say(picking ? "" : "In die Karte klicken, um den Start zu setzen."); }}>
                    {picking ? "Klick auf die Karte …" : "Auf der Karte wählen"}
                  </button>
                  <button type="button" className="rp-btn" onClick={locate}>Mein Standort</button>
                </div>
                <label className="rp-label">Ort als Start, z. B. dein Hotel
                  <select value={plan.start?.placeId ?? ""} onChange={(e) => {
                    const p = byId.get(Number(e.target.value));
                    if (p) setStart({ lat: p.lat, lng: p.lng, placeId: p.id, label: p.name });
                  }}>
                    <option value="">Ort auswählen …</option>
                    {hotels.length > 0 && <optgroup label={PLACE_TYPES.hotel.plural}>{hotels.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
                    <optgroup label="Weitere Orte">{others.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
                  </select>
                </label>
              </fieldset>

              <fieldset className="rp-block rp-modes">
                <legend>Unterwegs mit</legend>
                {Object.entries(TRAVEL_MODES).map(([k, v]) => (
                  <label key={k} className="rp-chip"><input type="radio" name="rp-mode" checked={plan.mode === k} onChange={() => write({ mode: k as TravelMode })} /> {v}</label>
                ))}
              </fieldset>

              <div className="rp-block">
                <h3>Stopps <span className="rp-count">{stops.length}/{maxStops}</span></h3>
                {stops.length === 0 && <p className="rp-muted">Pins auf der Karte antippen und „Zur Route hinzufügen“ wählen, oder hier einen Ort auswählen.</p>}
                <ol className="rp-stops">
                  {stops.map((s, i) => {
                    const p = byId.get(s.id)!;
                    return (
                      <li key={s.id} draggable className={drag === i ? "rp-drag" : undefined}
                        onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = "move"; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); if (drag != null) move(drag, i); setDrag(null); }}
                        onDragEnd={() => setDrag(null)}>
                        <span className="rp-num" aria-hidden="true">{i + 1}</span>
                        <span className="rp-name">
                          <a href={`/orte/${p.slug}`}>{p.name}</a>
                          <span className="rp-muted">{PLACE_TYPES[p.type as PlaceType]?.label}</span>
                        </span>
                        <label className="rp-dwell"><span className="sr-only">Verweildauer in Minuten für {p.name}</span>
                          <input type="number" min={0} max={720} step={5} inputMode="numeric" value={dwellOf(s)} onChange={(e) => setDwell(s.id, e.target.value)} />
                          <span aria-hidden="true">min</span>
                        </label>
                        <span className="rp-tools">
                          <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`${p.name} nach oben`}>↑</button>
                          <button type="button" onClick={() => move(i, i + 1)} disabled={i === stops.length - 1} aria-label={`${p.name} nach unten`}>↓</button>
                          <button type="button" onClick={() => toggle(s.id)} aria-label={`${p.name} entfernen`}>×</button>
                        </span>
                      </li>
                    );
                  })}
                </ol>
                {stops.length < maxStops && (
                  <label className="rp-label">Ort hinzufügen
                    <select value="" onChange={(e) => e.target.value && toggle(Number(e.target.value))}>
                      <option value="">Ort auswählen …</option>
                      {groups.map((g) => (
                        <optgroup key={g} label={PLACE_TYPES[g as PlaceType]?.plural ?? g}>
                          {free.filter((p) => p.type === g).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                )}
                <label className="rp-check"><input type="checkbox" checked={plan.optimize} onChange={(e) => write({ optimize: e.target.checked })} /> Reihenfolge optimieren</label>
                <label className="rp-check"><input type="checkbox" checked={plan.back} onChange={(e) => write({ back: e.target.checked })} /> Zurück zum Startpunkt</label>
              </div>

              <div className="rp-row">
                <button type="button" className="tbutton rp-go" onClick={() => void calculate()} disabled={!ready || status.kind === "busy"}>
                  {status.kind === "busy" ? "Berechnet …" : "Route berechnen"}
                </button>
                {(stops.length > 0 || plan.start) && (
                  <button type="button" className="rp-btn" onClick={() => { savePlan({ ...plan, start: null, stops: [] }, storageKey); setResult(null); setLive(false); say("Route geleert."); }}>Alles leeren</button>
                )}
              </div>
              {!ready && <p className="rp-muted">Für eine Route brauchst du einen Startpunkt und mindestens einen Stopp.</p>}
              {ready && !live && <p className="rp-muted">Berechnet wird erst, wenn du „Route berechnen“ wählst.</p>}
              {status.kind === "error" && <p className="rp-error" role="alert">{status.text}</p>}

              {current && (
                <div className="rp-result" aria-live="polite">
                  <h3>Ergebnis</h3>
                  <ol className="rp-legs">
                    {current.legs.map((l, i) => (
                      <li key={i}>
                        <span>{name(l.from)} → {name(l.to)}</span>
                        <strong>{fmtDuration(l.seconds)} · {fmtDistance(l.meters)}</strong>
                        {l.winding && <em>Kurvenreiche Bergstrecke, eher mehr Zeit einplanen.</em>}
                      </li>
                    ))}
                  </ol>
                  <dl className="rp-sum">
                    <div><dt>Fahrzeit</dt><dd>{fmtDuration(current.seconds)}</dd></div>
                    <div><dt>Strecke</dt><dd>{fmtDistance(current.meters)}</dd></div>
                    <div><dt>Verweildauer</dt><dd>{dwellSum ? fmtDuration(dwellSum) : "–"}</dd></div>
                    <div><dt>Tag gesamt</dt><dd>{fmtDuration(current.seconds + dwellSum)}</dd></div>
                  </dl>
                  <ul className="rp-hints">
                    <li>Zeiten sind Schätzungen ohne Live-Verkehr{factor > 1 ? ` (mit ${Math.round((factor - 1) * 100)} % Zuschlag für Bergstraßen)` : ""}.</li>
                    <li>Bitte Öffnungszeiten prüfen{stops.some((s) => byId.get(s.id)?.hours) ? ", sie stehen auf den Seiten der Orte" : ""}.</li>
                  </ul>

                  <div className="rp-export">
                    <button type="button" className="rp-btn" onClick={shareLink}>Link teilen</button>
                    <button type="button" className="rp-btn" onClick={downloadGpx}>GPX</button>
                    <button type="button" className="rp-btn" onClick={() => pdfForm.current?.submit()}>PDF</button>
                    <button type="button" className="rp-btn" aria-expanded={google} onClick={() => setGoogle(!google)}>In Google Maps öffnen</button>
                  </div>
                  {share && <label className="rp-label">Teilen-Link<input readOnly value={share} onFocus={(e) => e.target.select()} /></label>}
                  {google && (
                    <div className="rp-google" role="note">
                      <p>Google Maps ist ein externer Dienst. Beim Öffnen gehen Start und Stopps an Google.{tooManyForGoogle ? ` Google übernimmt höchstens ${GOOGLE_MAX_WAYPOINTS} Zwischenstopps.` : ""}</p>
                      <div className="rp-row">
                        <a className="tbutton" href={googleUrl()} target="_blank" rel="noopener noreferrer" onClick={() => setGoogle(false)}>Weiter zu Google Maps</a>
                        <button type="button" className="rp-btn" onClick={() => setGoogle(false)}>Abbrechen</button>
                      </div>
                    </div>
                  )}
                  <form ref={pdfForm} method="post" action="/api/route/pdf" hidden>
                    <input type="hidden" name="r" value={pdfData} />
                  </form>
                </div>
              )}
              <p className="rp-muted rp-privacy">Deine Planung bleibt in diesem Browser. Zum Berechnen gehen Start und Stopps an unseren Server, gespeichert wird dort nichts.</p>
            </div>
      </section>
    </div>
  );
}
